import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, forkJoin, map, merge, mergeMap, of, switchMap, tap } from 'rxjs';
import {
  ApiEnvelope,
  LabOrderResultsPage,
  UnlinkedDismissResult,
  UnlinkedLinkResult,
  UnlinkedPayload,
  UNLINKED_FIRST_PAGE,
  unlinkedPageNumbers,
  UnlinkedResultDetail,
  UnlinkedResultDto,
  UnlinkedResultPage
} from '../models/unlinked-result.model';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

/** Rows in these states have already been resolved and drop off the worklist. */
const RESOLVED_UNLINKED_STATES = ['auto_linked', 'manually_linked', 'dismissed'];


@Injectable({
  providedIn: 'root'
})
export class ResultsService {
  private apiUrl = 'api';
  private resultsSubject = new BehaviorSubject<any[]>([]);
  private unlinkedSubject = new BehaviorSubject<UnlinkedResultDto[]>([]);
  /**
   * OUR OWN vendor code, as the server reports it. Read off the ENVELOPE of whichever list
   * endpoint answered last; both send it. `null` until a response has been seen, and a
   * consumer that has not seen one suppresses NOTHING rather than guessing.
   */
  private vendorDoctorPlatformCodeSubject = new BehaviorSubject<string | null>(null);

  results$ = this.resultsSubject.asObservable();
  /** Inbound pathology results that arrived without a MedicAI lab order and still need a human. */
  unlinked$ = this.unlinkedSubject.asObservable();
  /** The `vendorDoctors` badge whose `code` equals this must not be rendered. See the model. */
  vendorDoctorPlatformCode$ = this.vendorDoctorPlatformCodeSubject.asObservable();

  groupId: any;
  currentPage = 1;
  pageSize = 500;
  isLoading = false;

  unlinkedPageSize = 200;
  isLoadingUnlinked = false;

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1;
          this.resultsSubject.next([]);
          this.unlinkedSubject.next([]);
          // switchMap tore down any in-flight load; clear the guard or it wedges forever.
          this.isLoadingUnlinked = false;
          return merge(this.loadResults(), this.loadUnlinked());
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {
    // Listen for when a new lab order is created
    this.socketService.listen('labOrderCreated', 'results-service').subscribe((data) => {
      const current = this.resultsSubject.getValue();
      // Only add if it belongs to the current group
      if (data.groupId === this.groupId) {
        const labOrder = data.labOrder || data;
        // Check if order already exists
        const index = current.findIndex(r => r.id === labOrder.id);
        if (index === -1) {
          // Add new order to the list
          this.resultsSubject.next([labOrder, ...current]);
        }
      }
    });

    // Listen for lab order updates (full update with all data)
    this.socketService.listen('labOrderUpdated', 'results-service').subscribe((data) => {
      const current = this.resultsSubject.getValue();
      const labOrder = data.labOrder || data;
      // Only update if it belongs to the current group
      if (labOrder.groupId === this.groupId || data.groupId === this.groupId) {
        const index = current.findIndex(r => r.id === labOrder.id);
        if (index !== -1) {
          // Update existing order with full data
          current[index] = { ...labOrder };
          this.resultsSubject.next([...current]);
        } else {
          // Add new order if not in list (may have been created in another session)
          this.resultsSubject.next([labOrder, ...current]);
        }
      }
    });

    // Listen for lab order status updates (from worker - pending, ordered, in_progress)
    this.socketService.listen('labOrderStatusUpdated', 'results-service').subscribe((updated) => {
      const current = this.resultsSubject.getValue();
      // Only update if it belongs to the current group
      if (updated.groupId === this.groupId) {
        const index = current.findIndex(r => r.id === updated.orderId || r.id === updated.id);
        if (index !== -1) {
          // Update status and preserve Patient information if provided
          current[index] = { 
            ...current[index], 
            status: updated.status,
            Patient: updated.Patient || current[index].Patient // Preserve or update Patient info
          };
          this.resultsSubject.next([...current]);
        } else {
          // Add new pending/ordered order if not already in list
          this.resultsSubject.next([...current, { ...updated, id: updated.orderId || updated.id }]);
        }
      }
    });

    // Listen for when a lab order fails
    this.socketService.listen('labOrderFailed', 'results-service').subscribe((failedOrder) => {
      const current = this.resultsSubject.getValue();
      // Only update if it belongs to the current group
      if (failedOrder.groupId === this.groupId) {
        const index = current.findIndex(r => r.id === failedOrder.orderId || r.id === failedOrder.id);
        if (index !== -1) {
          current[index] = { 
            ...current[index], 
            status: 'failed', 
            errorMessage: failedOrder.error,
            Patient: failedOrder.Patient || current[index].Patient // Preserve or update Patient info
          };
          this.resultsSubject.next([...current]);
        } else {
          // Add new failed order if not already in list
          this.resultsSubject.next([...current, { ...failedOrder, status: 'failed', id: failedOrder.orderId || failedOrder.id }]);
        }
      }
    });

    // Listen for when a lab order is completed with results
    this.socketService.listen('labOrderCompleted', 'results-service').subscribe((completedOrder) => {
      const current = this.resultsSubject.getValue();
      // Only update if it belongs to the current group
      if (completedOrder.groupId === this.groupId) {
        const index = current.findIndex(r => r.id === completedOrder.orderId || r.id === completedOrder.id);
        if (index !== -1) {
          current[index] = {
            ...current[index],
            status: 'completed',
            labResults: completedOrder.results,
            resultSummary: completedOrder.resultSummary,
            resultDate: completedOrder.resultDate || new Date().toISOString(),
            reviewedAt: null, // New results are unseen
            Patient: completedOrder.Patient || current[index].Patient // Preserve or update Patient info
          };
          this.resultsSubject.next([...current]);
        } else {
          // Add new completed order if not already in list
          this.resultsSubject.next([...current, {
            ...completedOrder,
            status: 'completed',
            id: completedOrder.orderId || completedOrder.id,
            reviewedAt: null
          }]);
        }
      }
    });

    // Listen for when lab results are manually added
    this.socketService.listen('labResultAdded', 'results-service').subscribe((data) => {
      const current = this.resultsSubject.getValue();
      const labOrder = data.labOrder || data;
      // Only update if it belongs to the current group
      if (labOrder.groupId === this.groupId || data.groupId === this.groupId) {
        const index = current.findIndex(r => r.id === labOrder.id);
        if (index !== -1) {
          // Update existing order with results
          current[index] = {
            ...current[index],
            ...labOrder,
            status: labOrder.status || 'completed',
            reviewedAt: labOrder.reviewedAt || null
          };
          this.resultsSubject.next([...current]);
        } else {
          // Add new order with results if not in list
          this.resultsSubject.next([labOrder, ...current]);
        }
      }
    });

    // Listen for when a lab order is deleted (soft delete)
    this.socketService.listen('labOrderDeleted', 'results-service').subscribe((deletedData) => {
      const current = this.resultsSubject.getValue();
      // Only remove if it belongs to the current group
      if (deletedData.groupId === this.groupId) {
        const orderId = deletedData.orderId || deletedData.id;
        const updated = current.filter(r => r.id !== orderId);
        this.resultsSubject.next(updated);
      }
    });

    // Listen for patient updates
    this.socketService.listen('patientUpdated', 'results-service').subscribe((updatedPatient) => {
      const current = this.resultsSubject.getValue();
      const updatedResults = current.map(item => {
        if (item.patientId === updatedPatient.id) {
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              diagnosis: updatedPatient.diagnosis,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });
      this.resultsSubject.next(updatedResults);
    });

    // ---- Unlinked pathology results (no MedicAI lab order) ----

    // A new unmatched result arrived from the vendor for this group.
    this.socketService.listen('unlinkedResultReceived', 'results-service').subscribe((data) => {
      if (!data || data.groupId !== this.groupId) {
        return;
      }
      const row: UnlinkedResultDto = data.unlinkedResult || data.result || data;
      if (!row?.id) {
        return;
      }
      // A resolved row must never re-enter the worklist (e.g. a REPRINT of something already linked).
      if (row.resolutionState && RESOLVED_UNLINKED_STATES.includes(row.resolutionState)) {
        this.removeUnlinkedLocally(row.id);
        return;
      }
      const current = this.unlinkedSubject.getValue();
      const index = current.findIndex(r => r.id === row.id);
      if (index === -1) {
        this.unlinkedSubject.next([row, ...current]);
      } else {
        // Supersession: the same specimen re-sent as INTERIM -> FINAL -> REPRINT.
        const updated = [...current];
        updated[index] = { ...updated[index], ...row };
        this.unlinkedSubject.next(updated);
      }
    });

    // Someone (here or in another session) linked it to a patient.
    this.socketService.listen('unlinkedResultLinked', 'results-service').subscribe((data) => {
      if (!data || data.groupId !== this.groupId) {
        return;
      }
      const id = data.id || data.unlinkedResultId || data.unlinkedResult?.id;
      if (id) {
        this.removeUnlinkedLocally(id);
      }
    });

    // Someone dismissed it with a reason.
    this.socketService.listen('unlinkedResultDismissed', 'results-service').subscribe((data) => {
      if (!data || data.groupId !== this.groupId) {
        return;
      }
      const id = data.id || data.unlinkedResultId || data.unlinkedResult?.id;
      if (id) {
        this.removeUnlinkedLocally(id);
      }
    });
  }

  /**
   * Records the platform code an envelope reported.
   *
   * A response that does NOT carry one leaves the last known value alone. Clobbering it with
   * null would silently switch suppression off mid-session and start showing `00MEDICAI` as a
   * party on every badge set.
   */
  private setVendorDoctorPlatformCode(code: string | null | undefined): void {
    const next = typeof code === 'string' && code.trim() ? code.trim() : null;
    if (next === null || next === this.vendorDoctorPlatformCodeSubject.getValue()) {
      return;
    }
    this.vendorDoctorPlatformCodeSubject.next(next);
  }

  /** Drops a row from the worklist once it is linked or dismissed. Idempotent. */
  private removeUnlinkedLocally(id: string): void {
    const current = this.unlinkedSubject.getValue();
    const updated = current.filter(r => r.id !== id);
    if (updated.length !== current.length) {
      this.unlinkedSubject.next(updated);
    }
  }

  /**
   * Loads every unlinked result for the current group.
   *
   * Paging is ONE-BASED — the server computes `offset = (page - 1) * pageSize`, matching
   * `GET /results/group/:groupId` and `currentPage = 1` above. Requesting page 0 first
   * would skip nothing but then re-read the first page as page 1 and drop the tail;
   * do not "fix" this to 0.
   *
   * Unlike `loadResults()` this pages deterministically: the first response carries `total`,
   * so the remaining page count is known up front instead of being inferred from a short page.
   */
  loadUnlinked(): Observable<UnlinkedResultDto[]> {
    const groupId = this.groupId;
    if (!groupId) {
      return of([]);
    }
    if (this.isLoadingUnlinked) {
      return EMPTY;
    }
    this.isLoadingUnlinked = true;

    return this.getUnlinkedByGroup(groupId, UNLINKED_FIRST_PAGE, this.unlinkedPageSize).pipe(
      switchMap((first: UnlinkedResultPage) => {
        this.setVendorDoctorPlatformCode(first?.vendorDoctorPlatformCode);
        const firstItems = first?.data ?? [];
        const pageSize = Number(first?.pageSize) > 0 ? Number(first.pageSize) : this.unlinkedPageSize;
        const total = Number.isFinite(Number(first?.total)) ? Number(first.total) : firstItems.length;
        const pages = unlinkedPageNumbers(total, pageSize);

        if (pages.length <= 1 || firstItems.length === 0) {
          return of(firstItems);
        }

        const rest: Observable<UnlinkedResultPage>[] = pages
          .slice(1)
          .map(page => this.getUnlinkedByGroup(groupId, page, pageSize));
        return forkJoin(rest).pipe(
          map(laterPages => firstItems.concat(...laterPages.map(p => p?.data ?? [])))
        );
      }),
      map(items => items.filter(item =>
        !item?.resolutionState || !RESOLVED_UNLINKED_STATES.includes(item.resolutionState)
      )),
      tap(items => {
        this.isLoadingUnlinked = false;
        // A group switch mid-flight must not overwrite the new group's list.
        if (this.groupId === groupId) {
          this.unlinkedSubject.next(items);
        }
      }),
      catchError(() => {
        this.isLoadingUnlinked = false;
        return of([]);
      })
    );
  }

  getUnlinkedByGroup(groupId: string, page: number, pageSize: number): Observable<UnlinkedResultPage> {
    const token = localStorage.getItem('token');
    return this.http.get<UnlinkedResultPage>(
      `${this.apiUrl}/results/group/${groupId}/unlinked?page=${page}&pageSize=${pageSize}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  /** Links one unlinked result to a patient. The server materialises a normal PatientLabOrder. */
  linkUnlinkedResult(groupId: string, id: string, patientId: string): Observable<ApiEnvelope<UnlinkedLinkResult>> {
    const token = localStorage.getItem('token');
    return this.http.post<ApiEnvelope<UnlinkedLinkResult>>(
      `${this.apiUrl}/results/group/${groupId}/unlinked/${id}/link`,
      { patientId },
      { headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      tap(res => {
        if (res?.success) {
          this.removeUnlinkedLocally(id);
        }
      })
    );
  }

  /** Dismisses one unlinked result. `reason` is required by the server. */
  dismissUnlinkedResult(groupId: string, id: string, reason: string): Observable<ApiEnvelope<UnlinkedDismissResult>> {
    const token = localStorage.getItem('token');
    return this.http.post<ApiEnvelope<UnlinkedDismissResult>>(
      `${this.apiUrl}/results/group/${groupId}/unlinked/${id}/dismiss`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      tap(res => {
        if (res?.success) {
          this.removeUnlinkedLocally(id);
        }
      })
    );
  }

  /** Raw vendor payload — fetched only when a human opens one, never with the list. */
  getUnlinkedPayload(groupId: string, id: string): Observable<ApiEnvelope<UnlinkedPayload>> {
    const token = localStorage.getItem('token');
    return this.http.get<ApiEnvelope<UnlinkedPayload>>(
      `${this.apiUrl}/results/group/${groupId}/unlinked/${id}/payload`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  /**
   * The parsed document for one unlinked result. `data.labResults` has the same shape as
   * `PatientLabOrder.labResults`, so `LabResultsDialogComponent` can render it directly.
   * PHI — fetched only when a human opens a row, never with the list.
   */
  getUnlinkedResultDetail(groupId: string, id: string): Observable<ApiEnvelope<UnlinkedResultDetail>> {
    const token = localStorage.getItem('token');
    return this.http.get<ApiEnvelope<UnlinkedResultDetail>>(
      `${this.apiUrl}/results/group/${groupId}/unlinked/${id}/results`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  /**
   * Server-rendered PDF of the unlinked report.
   *
   * Deliberately mirrors `PatientLabService.downloadLabResultsAsPdf` — the server renders the
   * document and returns a blob; the client does not build a PDF of its own. Error bodies come
   * back as a Blob rather than JSON, so callers must use `unlinkedBlobErrorMessage`.
   */
  downloadUnlinkedResultPdf(groupId: string, id: string): Observable<Blob> {
    const token = localStorage.getItem('token');
    return this.http.get(
      `${this.apiUrl}/results/group/${groupId}/unlinked/${id}/download-pdf`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }
    );
  }

  loadResults(): Observable<any[]> {
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    return this.getResultsByGroup(this.groupId, this.currentPage, this.pageSize).pipe(
      mergeMap((res: LabOrderResultsPage) => {
        this.setVendorDoctorPlatformCode(res?.vendorDoctorPlatformCode);
        const items = res.data;
        const current = this.resultsSubject.getValue();
        const updated = [...current, ...items];
        this.resultsSubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadResults();
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoading = false;
        return of([]);
      })
    );
  }

  /**
   * The response is an ENVELOPE, not an array — it always was, and `loadResults` reads
   * `res.data`. Typed as one now so `vendorDoctorPlatformCode`, which lives on the envelope,
   * cannot be read off a row by mistake.
   */
  getResultsByGroup(groupId: string, page: number, pageSize: number): Observable<LabOrderResultsPage> {
    const token = localStorage.getItem('token');
    return this.http.get<LabOrderResultsPage>(`${this.apiUrl}/results/group/${groupId}?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

}

